import axios from 'axios';
import { 
  GameDetails, 
  DownloadLinksResponse, 
  LegacyGenreResponse,
  GenresResponse, 
  SearchParams, 
  PaginationParams 
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-combined-rom3.onrender.com';

// Cache simple en mémoire
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // Réduit le timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour optimiser les requêtes
api.interceptors.request.use((config) => {
  const cacheKey = `${config.url}-${JSON.stringify(config.params)}`;
  const cached = cache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('🚀 Using cached data for:', config.url);
    return Promise.reject({ cached: true, data: cached.data });
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Cache la réponse
    const cacheKey = `${response.config.url}-${JSON.stringify(response.config.params)}`;
    cache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });
    return response;
  },
  (error) => {
    if (error.cached) {
      return Promise.resolve({ data: error.data });
    }
    return Promise.reject(error);
  }
);

// Optimisation des conversions
const convertToLegacyFormat = (response: any): LegacyGenreResponse => {
  if (Array.isArray(response)) {
    return {
      games: response,
      current_page: 1,
      total_pages: 1,
      total_games: response.length,
      has_next: false,
      has_previous: false
    };
  }
  
  if (response?.pagination) {
    const estimatedGamesPerPage = response.games?.length || 20;
    const totalGames = estimatedGamesPerPage * response.pagination.total_pages;
    
    return {
      games: response.games || [],
      current_page: response.pagination.current_page,
      total_pages: response.pagination.total_pages,
      total_games: totalGames,
      has_next: response.pagination.has_next,
      has_previous: response.pagination.has_previous
    };
  }
  
  return response;
};

// Services optimisés
export const gameService = {
  getFeaturedGames: async (params: PaginationParams = {}): Promise<LegacyGenreResponse> => {
    const response = await api.get('/api/featured', {
      params: { 
        page: params.page || 1,
        limit: 20 // Limite fixe pour la performance
      }
    });
    return convertToLegacyFormat(response.data);
  },

  getGenreGames: async (genre: string, params: PaginationParams = {}): Promise<LegacyGenreResponse> => {
    const response = await api.get(`/api/genre/${genre}`, {
      params: { page: params.page || 1 }
    });
    return convertToLegacyFormat(response.data);
  },

  getAvailableGenres: async (): Promise<GenresResponse> => {
    const response = await api.get('/api/genres');
    return response.data;
  },

  getRepackGames: async (params: PaginationParams = {}): Promise<LegacyGenreResponse> => {
    const response = await api.get('/api/repack', {
      params: { page: params.page || 1 }
    });
    return convertToLegacyFormat(response.data);
  },

  getOnlineGames: async (params: PaginationParams = {}): Promise<LegacyGenreResponse> => {
    const response = await api.get('/api/online', {
      params: { page: params.page || 1 }
    });
    return convertToLegacyFormat(response.data);
  },

  getUpdatesGames: async (params: PaginationParams = {}): Promise<LegacyGenreResponse> => {
    const response = await api.get('/api/updates', {
      params: { page: params.page || 1 }
    });
    return convertToLegacyFormat(response.data);
  },

  getGameDetails: async (gamePath: string): Promise<GameDetails> => {
    const response = await api.get(`/api/details/${gamePath}`);
    return response.data;
  },

  searchGames: async (params: SearchParams): Promise<LegacyGenreResponse> => {
    const response = await api.get('/api/search', {
      params: {
        q: params.q,
        page: params.page || 1
      }
    });
    return convertToLegacyFormat(response.data);
  },

  getDownloadLinks: async (url: string): Promise<DownloadLinksResponse> => {
    const response = await api.get('/api/getlinks', {
      params: { url }
    });
    return response.data;
  }
};

export default api;